// ═══════════════════════════════════════════════════════════════════
//  telemetryStore — Shared in-memory event log for the search +
//                   minimap (and future) subsystems.
// ═══════════════════════════════════════════════════════════════════
//
//  Both `lib/search/telemetry.ts` and `lib/minimap/telemetry.ts` had
//  drifted into ~identical implementations. This factory unifies them:
//  pass a debug-flag name + optional console prefix, get back the
//  same `{ log, get, clear }` triple every subsystem needs.
//
//  All stores share:
//    - `kind: string` discriminator on every event (enforced by the
//      generic constraint)
//    - 500-event ring buffer (FIFO eviction)
//    - Optional `window.__FW_<DEBUG_FLAG>__` toggle for stderr/console
//      breadcrumbs
//
// ═══════════════════════════════════════════════════════════════════

const DEFAULT_CAP = 500;

export interface TelemetryStoreOpts {
  /** Window-level boolean flag name (e.g. `__FW_SEARCH_DEBUG`). */
  debugFlag?: string;
  /** Console prefix for debug breadcrumbs. */
  debugPrefix?: string;
  /** Override the default ring buffer size. */
  cap?: number;
}

export interface TelemetryStore<T extends { kind: string }> {
  log(ev: T): void;
  get(): readonly T[];
  clear(): void;
  /** Atomically returns the buffered events AND clears the buffer.
   *  Used by the flush layer to ship events to a backend without
   *  losing newly-logged events that arrive mid-flush. */
  getAndClear(): T[];
}

export function createTelemetryStore<T extends { kind: string }>(
  opts: TelemetryStoreOpts = {},
): TelemetryStore<T> {
  const cap = opts.cap ?? DEFAULT_CAP;
  const buffer: T[] = [];
  const debugFlag = opts.debugFlag;
  const prefix = opts.debugPrefix ?? '[telemetry]';

  return {
    log(ev: T) {
      buffer.push(ev);
      if (buffer.length > cap) buffer.splice(0, buffer.length - cap);
      if (
        debugFlag &&
        typeof window !== 'undefined' &&
        (window as unknown as Record<string, unknown>)[debugFlag] === true
      ) {
         
        console.debug(prefix, ev);
      }
    },
    get() {
      return buffer;
    },
    clear() {
      buffer.length = 0;
    },
    getAndClear() {
      const out = buffer.slice();
      buffer.length = 0;
      return out;
    },
  };
}
